import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { get as _get } from 'lodash';

// Models

// Store
import {
  Claim,
  ClaimDetailServiceRequestModel,
  MonetaryTransaction
} from '@amfam/claim/data-access';
import { ClaimPhotoSummary } from '@amfam/claim/photo-upload/feature';
import {
  ClaimServicesConstants,
  ClaimServicesStatus,
  ServiceType
} from '@amfam/claim/post-fnol/services-data-access';
import { ClaimUtilService } from '@amfam/claim/shared/utility';
import { QuickLink } from '@amfam/shared/ui/quick-links';
import { CopyService } from '@amfam/shared/utility/shared-services';

import {
  ClaimActivity,
  ClaimGeneralDataModel,
  ClaimProgress,
  ClaimRentalDataModel,
  ClaimTowingRepairDataModel
} from '../../models';
import { ClaimDetailConstant } from '../../models/claim-detail-constants';
import { ClaimStatusService } from '../../services/claim-status-service';

@Component({
  selector: 'ds-auto-claim-status',
  templateUrl: './auto-claim-status.component.html',
  styleUrls: ['./auto-claim-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AutoClaimStatusComponent implements OnInit {
  @Input() claim: Claim;
  @Input() claimServices: ServiceType[];
  @Input() claimServicesStatus: ClaimServicesStatus;
  @Input() eligibleServicesError: boolean;
  @Input() photoList: ClaimPhotoSummary[];
  @Input() photosEnabled: boolean;
  @Input() viewPhotosAuthorized: boolean;
  @Input() uploadPhotosAuthorized: boolean;
  @Input() postFnolEnabled: boolean;
  @Input() isPartner = false;
  @Input() rentalPhoneNumber: string;

  @Output() routeToPostFnol = new EventEmitter();
  @Output() routeToPhotos = new EventEmitter();

  claimNumber: string;
  showServiceDisclaimerModal = false;
  quickLinksList = new Array<QuickLink>();
  displayContinueClaim = true;
  claimGeneralData: ClaimGeneralDataModel;
  hasGeneralData = false;
  rentalCarData: ClaimRentalDataModel;
  repairData: ClaimTowingRepairDataModel;
  claimActivityData: ClaimActivity[];
  claimProgressData: ClaimProgress;
  rentalServiceRequestData: ClaimDetailServiceRequestModel;
  paymentsData: MonetaryTransaction[] = [];
  towingAndRepairData: ClaimDetailServiceRequestModel[] = [];
  claimDetailConstant = ClaimDetailConstant;
  vehicleNotOnPolicy: string = this.copyService.getCopy('claims.fnol.vehicleNotOnPolicy');
  vehicleDesctiption: string;
  phoneNumber: string;

  summary = this.copyService.getCopy('shared.summary');
  photos = this.copyService.getCopy('shared.photos');
  payments = this.copyService.getCopy('shared.payments');
  rentalCar = this.copyService.getCopy('shared.rentalCar');
  towingAndRepairShops = this.copyService.getCopy('shared.towingAndRepairShops');

  constructor(
    private claimUtilService: ClaimUtilService,
    private claimStatusService: ClaimStatusService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.claimNumber = this.claim.claimNumber;

    this.displayContinueClaim =
      this.postFnolEnabled &&
      !this.eligibleServicesError &&
      ((this.claimServicesStatus.repairEligible && !this.claimServicesStatus.repairSelected) ||
        (this.claimServicesStatus.rentalEligible && !this.claimServicesStatus.rentalSelected));

    this.claimActivityData = this.claimStatusService.buildActivityData(
      this.claim,
      this.paymentsData,
      ClaimDetailConstant.AUTO,
      this.towingAndRepairData
    );

    this.rentalServiceRequestData = this.checkForRentalData(this.claim);

    this.claimGeneralData = this.claimStatusService.buildGeneralData(this.claim);
    this.claimProgressData = this.claimStatusService.buildProgressData(
      this.claim,
      ClaimDetailConstant.AUTO
    );
    this.rentalCarData = this.buildRentalCarData(this.claim);
    this.repairData = this.buildRepairData(this.claim);
    this.hasGeneralData = this.claimUtilService.checkObjPropsHasValue(this.claim);

    this.vehicleDesctiption =
      _get(this.claimGeneralData, 'vehicle.year', '') +
      ' ' +
      _get(this.claimGeneralData, 'vehicle.make', '') +
      ' ' +
      _get(this.claimGeneralData, 'vehicle.model', '');

    if (_get(this.claim, 'primaryFileHandler.phones', []).length > 0) {
      this.phoneNumber =
        _get(this.claim, 'primaryFileHandler.phones[0].areaCode', '') +
        '-' +
        _get(this.claim, 'primaryFileHandler.phones[0].number', '');

      if (_get(this.claim, 'primaryFileHandler.phones[0].extension')) {
        this.phoneNumber +=
          ' ext. ' + _get(this.claim, 'primaryFileHandler.phones[0].extension', '');
      }
    } else {
      this.phoneNumber = this.copyService.getCopy('shared.amfamPhoneNumericNumber');
    }
  }

  public buildRentalCarData(claim: Claim): ClaimRentalDataModel {
    const coverageAmount = _get(
      this.claimServices.find(
        serviceType => serviceType.serviceCode === ClaimServicesConstants.RENTAL
      ),
      'coverageDescription',
      ''
    ).split('/');
    return {
      claimProgress: claim.claimProgress,
      rentalLimits: {
        perDay: coverageAmount[0],
        perWeek: coverageAmount[1]
      },
      serviceRequestData: this.rentalServiceRequestData,
      isRentalEligible: this.claimServicesStatus.rentalEligible
    };
  }

  public buildPaymentsData(claim: Claim) {
    return {
      claimProgress: claim.claimProgress,
      monetaryTransactions: this.paymentsData
    };
  }

  public buildRepairData(claim: Claim): ClaimTowingRepairDataModel {
    return {
      claimProgress: claim.claimProgress,
      serviceRequestData: this.towingAndRepairData,
      isRepairEligible: this.claimServicesStatus.repairEligible
    };
  }

  public routeToPostFnolFlow() {
    this.routeToPostFnol.emit();
  }
  routeToPhotoUpload() {
    this.routeToPhotos.emit();
  }

  private checkForRentalData(claim: Claim): ClaimDetailServiceRequestModel {
    return claim.claimDetail.serviceRequests.find(
      serviceRequest =>
        serviceRequest.typeOfServiceRequestCode.toUpperCase() === ClaimDetailConstant.RENTAL
    );
  }
}
